import React, { useEffect, useState, useMemo, useRef } from "react";
import { Button, Modal, Form } from "react-bootstrap";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { createChat, resetChatCreate } from "../../actions/chatActions";
import { toast } from "react-toastify";
import Loader from "../Loader";
import useDebounce from "../../hooks/useDebounce";
import { selectFollowGraph } from "../../selectors/userSelectors";
import { CHAT_IMAGE_ACCEPT, CHAT_UNSUPPORTED_IMAGE_MESSAGE, isHeicLike } from "../../shared/imageFileContract";
import AvatarCropEditor from "../../shared/avatarCrop/AvatarCropEditor";
import { isChatLogoCropEligible } from "../../shared/avatarCrop/avatarCropEligibility";
import { SERVER_PREVIEW_CONFIRM_MESSAGE } from "../../shared/mediaPrepare/constants";
import {
  createPrepareSession,
  mapPrepareErrorMessage,
  prepareImageForEditor,
} from "../../shared/mediaPrepare/prepareImageForEditor";


export default function CreateChatModal() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [show, setShow] = useState(false);
  const [name, setName] = useState("");
  const [file, setFile] = useState(null);
  const [pendingCropSource, setPendingCropSource] = useState(null);
  const [prepareStatus, setPrepareStatus] = useState(null);
  const [logoError, setLogoError] = useState(null);
  const fileInputRef = useRef(null);
  const prepareSessionRef = useRef(null);
  const activeRunIdRef = useRef(0);

  useEffect(
    () => () => {
      // Unmount: abort + invalidate so late prepare completions cannot commit.
      prepareSessionRef.current?.abort();
      prepareSessionRef.current = null;
      activeRunIdRef.current += 1;
    },
    [],
  );

  const { following } = useSelector(selectFollowGraph);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 300);
  const [selected, setSelected] = useState([]);
  const inputRef = useRef(null);

  const normalize = (text) => text?.toLowerCase().replace(/\s+/g, "");

  const availableUsers = useMemo(() => {

    const selectedIds = new Set(
      selected.map(m => m.id)
    );

    const s = normalize(debouncedSearch);

    return following
      .filter(user => !selectedIds.has(user.id))
      .filter(user => {

        if (!s) return true;

        return (
          normalize(user.fullname).includes(s) ||
          normalize(user.email).includes(s)
        );

      });

  }, [following, selected, debouncedSearch]);

  const clearGroupOnlyFields = () => {
    prepareSessionRef.current?.abort();
    prepareSessionRef.current = null;
    activeRunIdRef.current += 1;
    setName("");
    setFile(null);
    setPendingCropSource(null);
    setPrepareStatus(null);
    setLogoError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const prevSelectedCountRef = useRef(0);

  useEffect(() => {
    const prev = prevSelectedCountRef.current;
    const next = selected.length;
    if (prev >= 2 && next < 2) {
      clearGroupOnlyFields();
    }
    prevSelectedCountRef.current = next;
  }, [selected.length]);

  const toggleUser = (user) => {
    setSelected((prev) => {
      const exists = prev.find((u) => u.id === user.id);
      if (exists) {
        return prev.filter((u) => u.id !== user.id);
      }
      return [...prev, user];
    });
  };

  const removeSelected = (id) => {
    setSelected((prev) => prev.filter((u) => u.id !== id));
  };

  const {
    loading: loadingChatCreate,
    error: errorChatCreate,
    createdChat,
  } = useSelector((state) => state.chatCreate);

  const isGroup = selected.length >= 2;
  const cropActive = Boolean(pendingCropSource);
  const preparePending = Boolean(
    prepareStatus &&
      (prepareStatus.state === "native_check_pending" ||
        prepareStatus.state === "server_preview_pending" ||
        prepareStatus.state === "selected"),
  );
  const createBlocked = cropActive || preparePending || loadingChatCreate;

  const startHeicPrepare = async (nextFile) => {
    prepareSessionRef.current?.abort();
    const session = createPrepareSession();
    prepareSessionRef.current = session;
    activeRunIdRef.current = session.runId;
    setLogoError(null);
    setPrepareStatus({ state: "selected", runId: session.runId });
    setPendingCropSource(null);
    setFile(null);

    const result = await prepareImageForEditor(nextFile, {
      surface: "chat_logo",
      runId: session.runId,
      signal: session.signal,
      confirmMessage: SERVER_PREVIEW_CONFIRM_MESSAGE,
      isCurrent: () => activeRunIdRef.current === session.runId,
      onStateChange: (next) => {
        if (activeRunIdRef.current !== session.runId) return;
        setPrepareStatus(next);
      },
    });

    if (activeRunIdRef.current !== session.runId) return;

    if (result.kind === "native" || result.kind === "server") {
      setPrepareStatus({ state: result.state, runId: session.runId });
      setPendingCropSource(result.file);
      setLogoError(null);
      return;
    }
    if (result.kind === "cancelled") {
      setPrepareStatus(null);
      setPendingCropSource(null);
      return;
    }
    setPrepareStatus(null);
    setPendingCropSource(null);
    setLogoError(mapPrepareErrorMessage(result.code, result.detail));
  };

  const handleLogoChange = (event) => {
    const next = event.target.files && event.target.files[0];
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    if (!next) {
      return;
    }
    if (isHeicLike(next)) {
      startHeicPrepare(next);
      return;
    }
    if (isChatLogoCropEligible(next)) {
      prepareSessionRef.current?.abort();
      activeRunIdRef.current += 1;
      setPrepareStatus(null);
      setLogoError(null);
      setPendingCropSource(next);
      return;
    }
    // Truthful unsupported feedback — same semantics as profile picker.
    prepareSessionRef.current?.abort();
    activeRunIdRef.current += 1;
    setPendingCropSource(null);
    setPrepareStatus(null);
    setFile(null);
    setLogoError(CHAT_UNSUPPORTED_IMAGE_MESSAGE);
  };

  const handleCropApply = (croppedFile) => {
    setPendingCropSource(null);
    setPrepareStatus(null);
    setLogoError(null);
    setFile(croppedFile);
  };

  const handleCropCancel = () => {
    setPendingCropSource(null);
    setPrepareStatus(null);
  };

  const handleCreateChat = () => {
    if (!selected.length) return;
    if (isGroup && !name.trim()) return;
    if (createBlocked) return;
    // Private create must not submit hidden group-only name/photo.
    const nextName = isGroup ? name : "";
    const nextFile = isGroup ? file : null;
    dispatch(createChat(selected.map((u) => u.id), nextName, nextFile));
  };

  const handleClose = () => {
    setShow(false);
    setSelected([]);
    clearGroupOnlyFields();
    setSearch("");
  };

  useEffect(() => {
    if (!createdChat?.id) return;
    toast.success("Чат успішно створено");
    setShow(false);
    setSelected([]);
    clearGroupOnlyFields();
    setSearch("");
    navigate(`/chats/${createdChat.id}`);
    dispatch(resetChatCreate());
  }, [createdChat, navigate, dispatch]);

  useEffect(() => {
    if (!errorChatCreate) return;
    toast.error(errorChatCreate);
    dispatch(resetChatCreate());
  }, [errorChatCreate, dispatch]);

  return (
    <>
      <button
        type="button"
        className="create-chat-btn"
        aria-label="Створити чат"
        onClick={() => setShow(true)}
      >
        <i className="fa-solid fa-plus" />
      </button>

      <Modal
        show={show}
        onHide={handleClose}
        aria-labelledby="contained-modal-title-vcenter"
        centered
        className="project-modal feature-chat-modal"
      >
        <Modal.Header>
          <Modal.Title>Створити новий чат</Modal.Title>
          <div className="modal-btn-close" onClick={handleClose}>
            <i className="fa-solid fa-xmark fa-2x"></i>
          </div>
        </Modal.Header>
        <Modal.Body>

          {/* SELECTED USERS */}
          <div className="member-selector">
            {selected.map(user => (
              <div
                key={user.id}
                className="selected-member"
              >
                <img
                  src={user.user_image || "/images/no-profile-picture.png"}
                  alt=""
                />
                {user.fullname}
                <span
                  style={{
                    marginLeft: "6px",
                    cursor: "pointer"
                  }}
                  onClick={() => removeSelected(user.id)}
                >
                  ✕
                </span>
              </div>

            ))}
            <input
              type="text"
              placeholder="Пошук..."
              ref={inputRef}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Backspace" && !search && selected.length) {
                  removeSelected(selected[selected.length - 1].id)
                }
              }}
            />
          </div>
          {/* USERS LIST */}
          <div className="user-list">
            <div className="mt-2">Список користувачів, за якими ви стежите</div>
            {availableUsers.map(user => {
              const checked = selected.some(u => u.id === user.id);
              return (
                <div
                  key={user.id}
                  className="user-in-list"
                  onClick={() => toggleUser(user)}
                >
                  <img
                    src={user.user_image || "/images/no-profile-picture.png"}
                    alt={user.fullname}
                  />
                  <div className="fullname">
                    {user.fullname}
                  </div>
                  <Form.Check
                    className="d-none"
                    checked={checked}
                    readOnly
                  />
                </div>
              );
            })}
            {availableUsers.length === 0 && (
              <div className="text-center text-muted">
                Немає користувачів
              </div>
            )}
          </div>

          {isGroup ? (
            <>
              <Form.Group className="my-3" controlId="formBasicEmail">
                <Form.Label>
                  Назва чату <span className="req" aria-hidden="true">*</span>
                </Form.Label>
                <Form.Control
                  required
                  type="text"
                  placeholder="Введіть назву"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  aria-required="true"
                />
              </Form.Group>

              <Form.Group controlId="formFile" className="mb-3">
                <Form.Label>Фотографія</Form.Label>
                <Form.Control
                  type="file"
                  accept={CHAT_IMAGE_ACCEPT}
                  ref={fileInputRef}
                  onChange={handleLogoChange}
                  data-field="chat-create-image"
                />
                {preparePending && (
                  <Form.Text className="d-block mt-2" role="status" data-prepare-state={prepareStatus?.state}>
                    {prepareStatus?.state === "server_preview_pending"
                      ? "Підготовка попереднього перегляду на сервері…"
                      : "Перевірка зображення…"}
                  </Form.Text>
                )}
                {logoError && (
                  <Form.Text className="d-block mt-2 text-danger" role="alert">
                    {logoError}
                  </Form.Text>
                )}
                {file && !cropActive && !preparePending && (
                  <Form.Text className="d-block mt-2" role="status">
                    Обрано: {file.name}
                  </Form.Text>
                )}
              </Form.Group>

              {cropActive && (
                <AvatarCropEditor
                  sourceFile={pendingCropSource}
                  title="Обрізати фото чату"
                  onApply={handleCropApply}
                  onCancel={handleCropCancel}
                />
              )}
            </>
          ) : null}

        </Modal.Body>
        <Modal.Footer>
          {loadingChatCreate ? (
            <Loader />
          ) : (
            <>
              <Button variant="secondary" onClick={handleClose}>
                Закрити
              </Button>
              <Button
                variant="primary"
                onClick={handleCreateChat}
                disabled={createBlocked}
              >
                Створити
              </Button>
            </>
          )}
        </Modal.Footer>
      </Modal>
    </>
  )
}
